/**
 * "Project session" mode for Quick Chat: a chat backed by a *repo-attached*
 * managed sandbox instead of today's repo-less chat sandbox.
 *
 * Mission Control treats the `github-sandbox` environment id as a provisioning
 * sentinel and routes the task through the sandbox agent, which clones the
 * attached repository into `/workspaces/<repo>` before the daemon starts. Two
 * consequences drive everything in this module:
 *
 *  - the daemon is launched with that clone as its workspace root, so a project
 *    session must NOT send a client-chosen `workingDirectory`;
 *  - Mission Control's `/connect` mints a *repo-scoped* user-to-server token for
 *    a session that has a repository, so a project session must NOT override the
 *    AHP `authenticate` token with chat's data token. That token is read/write,
 *    but it is unscoped — carrying no repository grant, it cannot authenticate
 *    git operations against the clone.
 *
 * The repo-less path is untouched: a runtime only enters project mode when a
 * {@link ChatProject} is resolved for it.
 */

/** The repository a chat session is attached to. */
export interface ChatProject {
  owner: string
  name: string
  /** Credential-compatible HTTPS clone URL returned by Mission Control. */
  cloneUrl?: string
  /** Base ref the sandbox clone checks out. Omit for the repository default branch. */
  baseRef?: string
}

/**
 * On-demand managed-sandbox sentinel. Mission Control resolves this into freshly
 * provisioned compute (rather than binding an existing environment) and returns
 * the concrete environment id on the created session.
 */
export const SANDBOX_ENVIRONMENT_ID = 'github-sandbox'

/**
 * Agent slug repo-attached chat sessions are provisioned and reconnected under.
 * Mission Control already forces this slug for `github-sandbox` tasks; sending it
 * on the connect path too keeps the `dotcom-chat` data-token reseal — which would
 * replace the repo-scoped token with an unscoped one that cannot drive git — off
 * the project path.
 */
export const PROJECT_SESSION_AGENT_SLUG = 'copilot-developer-sandbox'

/** Split an `owner/repo` name. Returns `undefined` for anything else. */
export function parseProjectNwo(nwo: string | undefined): ChatProject | undefined {
  if (!nwo) return undefined
  const separator = nwo.indexOf('/')
  if (separator <= 0) return undefined
  const owner = nwo.slice(0, separator)
  const name = nwo.slice(separator + 1)
  if (!owner || !name || name.includes('/')) return undefined
  return {owner, name}
}
